import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { sumBy } from "lodash-es";

import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

export const useTicketQueries = () => {
  const api = useApi();
  const projectId = useProjectId();

  return {
    listPerProject: (query: ApiQueryParams<"getAdminTicketsV1">) => {
      const limit = 30;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.TICKETS_FEED(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getAdminTicketsV1({
              ...query,
              Limit: limit,
              Offset: pageParam * limit,
            })
            .then(commonAPIDataSelector),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
          if (!lastPage.hasMore) {
            return undefined;
          }

          return pages.length;
        },
      });
    },
    listPerPortfolio: (query: ApiQueryParams<"getAdminPortfolioTicketsV1">) => {
      const limit = 30;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_TICKETS_LIST(query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getAdminPortfolioTicketsV1({
              ...query,
              Limit: limit,
              Offset: pageParam * limit,
            })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
          if (!lastPage.hasMore) {
            return undefined;
          }

          return pages.length;
        },
      });
    },
    details: (ticketId: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_DETAILS(projectId, ticketId),
        queryFn: () => api.getAdminTicketsDetailsV1(ticketId),
        select: commonAPIDataSelector,
      }),
    activitiesInfinite: ({ ticketId, sortDescending }: { ticketId: string; sortDescending?: boolean }) => {
      const limit = sortDescending ? 20 : 1000;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.TICKETS_DETAILS_ACTIVITIES(projectId, ticketId, {
          sortDescending,
        }),
        queryFn: ({ pageParam }) =>
          api
            .getAdminTicketsActivitiesV1(ticketId, {
              Offset: pageParam,
              Limit: limit,
              sortDescending,
            })
            .then((x) => x.data),
        initialPageParam: 0,
        getNextPageParam: (latestResponse, allResponses) => {
          const newOffset = sumBy(allResponses, (page) => page.items.length);

          return latestResponse?.hasMore ? newOffset : undefined;
        },
      });
    },
    responsibilitySuggestion: (ticketId: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_RESPONSIBILITY_SUGGESTION(projectId, ticketId),
        queryFn: () => api.getAdminTicketsResponsibilitySuggestionV1(ticketId),
        select: commonAPIDataSelector,
        retry: false,
        staleTime: Infinity,
      }),
    denialReplySuggestion: (ticketId: string, data: { additionalInfo?: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_DENIAL_REPLY_SUGGESTION(projectId, ticketId),
        queryFn: () => api.postTicketsSuggestionV1(ticketId, data),
        select: commonAPIDataSelector,
        staleTime: 30 * 60 * 1000,
      }),
    ratingFeedbackOptions: () =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKET_RATING_FEEDBACK_OPTIONS(projectId),
        queryFn: () => api.getTicketsFeedbackOptionsV1(),
        select: commonAPIDataSelector,
        staleTime: Infinity,
      }),
    ticketTranslation: (ticketId: string, languageId: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_DETAILS_TRANSLATION(projectId, ticketId, languageId),
        queryFn: () => api.getTicketsTranslationsDetailsV1(ticketId, languageId),
        select: commonAPIDataSelector,
        retry: false,
      }),
    activityTranslation: ({
      ticketId,
      commentId,
      languageId,
    }: {
      ticketId: string;
      commentId: string;
      languageId: string;
    }) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKET_COMMENT_TRANSLATION(projectId, ticketId, commentId, languageId),
        queryFn: () => api.getTicketsCommentsTranslationsDetailsV1(ticketId, commentId, languageId),
        select: commonAPIDataSelector,
        retry: false,
      }),
    categoryList: () =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKET_CATEGORIES(projectId),
        queryFn: () => api.getTicketCategoriesV1(),
        select: commonAPIDataSelector,
      }),
    categoryDetails: ({ categoryId }: { categoryId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKET_CATEGORY(projectId, categoryId),
        queryFn: () => api.getTicketCategoriesDetailsV1(categoryId),
        select: commonAPIDataSelector,
      }),
  };
};
